import BigNumber from 'bignumber.js';
import { FormatPipeline, type PipelineOptions } from '~/utils/formatters/prtials/formatPipeline';
import type { NumberParts } from '~/utils/formatters/types';

export class NumberFormatter {
  public static createPipeline(options: PipelineOptions<number>) {
    return new FormatPipeline<number>(options, NumberFormatter);
  }

  private static formatLongDecimals(value: string, limit: number): NumberParts {
    const int = parseInt(value.split('.')[0]).toFixed();
    let dec = value.split('.')[1];

    if (!dec || dec.length <= limit)
      return {
        integer: int,
        decimals: dec ?? '',
        startZeros: 0,
        variant: '',
        initialValue: value
      };

    let leadingZeroesCount = 0;
    for (const char of dec.split('')) {
      if (char === '0') leadingZeroesCount++;
      else break;
    }

    const reducer = leadingZeroesCount > 0 ? 1 : 0;
    dec = dec.slice(leadingZeroesCount);

    if (dec.length > limit - reducer) dec = dec.slice(0, limit - reducer);

    return {
      integer: int,
      decimals: dec,
      startZeros: leadingZeroesCount,
      variant: '',
      initialValue: value
    };
  }

  private static formatLongNumberDecimals(value: NumberParts, limit: number, skipRounding = false): NumberParts {
    const _format = (_value: string, divider: number, variant: string) => {
      if (new BigNumber(_value).gt(1_000_000_000_000_000)) _value = '1000000000000000';

      const [_int, _dec] = (Math.round(parseInt(_value) / Math.round(divider / 10)) / 10).toString().split('.');

      return {
        integer: _int,
        decimals: _dec ?? '',
        startZeros: 0,
        variant,
        initialValue: value.initialValue
      };
    };

    let nextVal = { ...value };

    const decLimit = limit - value.integer.length;

    if (decLimit < 1) {
      nextVal.decimals = '';
      nextVal.startZeros = 0;
    } else {
      nextVal.decimals = nextVal.decimals.slice(0, decLimit);
    }

    if (!skipRounding) {
      if (parseInt(nextVal.integer) >= 1_000_000_000_000_000)
        nextVal = _format(nextVal.integer, 1_000_000_000_000_000, 'st');
      if (parseInt(nextVal.integer) >= 1_000_000_000_000) nextVal = _format(nextVal.integer, 1_000_000_000_000, 't');
      if (parseInt(nextVal.integer) >= 1_000_000_000) nextVal = _format(nextVal.integer, 1_000_000_000, 'b');
      else if (parseInt(nextVal.integer) >= 1_000_000) nextVal = _format(nextVal.integer, 1_000_000, 'm');
      else if (parseInt(nextVal.integer) >= 10_000) nextVal = _format(nextVal.integer, 1000, 'k');
    }

    return nextVal;
  }

  private static prettifyInteger(value: NumberParts): NumberParts {
    const chunks: string[] = [];

    for (let i = value.integer.length; i > 0; i -= 3) {
      chunks.push(value.integer.slice(Math.max(i - 3, 0), Math.max(i, 0)));
    }

    chunks.reverse();

    value.integer = chunks.join(',').replace(/-,/g, '-').replace(/\+,/g, '+');
    return value;
  }

  public static formatNumber(
    value: string | number | BigNumber,
    decimalsLimit: number = 4,
    numberLimit: number = 5,
    skipRounding = false,
    hardDecimalsLimit = 18
  ) {
    const _number = this.cutDecimals(value, hardDecimalsLimit).toFixed();

    const parts = this.formatLongDecimals(_number, decimalsLimit);
    const format = this.formatLongNumberDecimals(parts, numberLimit, skipRounding);

    return this.prettifyInteger(format);
  }

  public static formatUSDNumber(_number: string | number | BigNumber) {
    const number = new BigNumber(_number);

    if (number.isNaN() || number.gte(Number.POSITIVE_INFINITY) || number.lte(Number.NEGATIVE_INFINITY)) {
      return this.formatNumber('0', 2, 7);
    }

    if (number.gt(0) && Number(number.toFixed(2)) === 0) {
      return this.formatNumber(number.toFixed(), 2, 7);
    }

    return this.formatNumber(number.toFixed(2), 2, 7);
  }

  public static formatPercent(_number: string | number | BigNumber, decimalsLimit: number = 1, hardDecimalsLimit = 18) {
    const number = new BigNumber(_number);

    if (number.isNaN() || number.gte(Number.POSITIVE_INFINITY) || number.lte(Number.NEGATIVE_INFINITY)) {
      return this.formatNumber('0', 2, 7);
    }

    return this.formatNumber(number.toFixed(), decimalsLimit, 7, false, hardDecimalsLimit);
  }

  public static formatStaticPercent(_number: string | number | BigNumber, dp: number = 1) {
    const number = new BigNumber(_number);

    if (number.isNaN() || number.gte(Number.POSITIVE_INFINITY) || number.lte(Number.NEGATIVE_INFINITY)) {
      return this.formatNumber('0', 2, 7);
    }

    return this.formatNumber(number.toFixed(dp), dp, 7);
  }

  public static cutDecimals(_number: string | number | BigNumber, dp: number) {
    const number = new BigNumber(_number).toFixed();

    const [int, decimals] = number.toString().split('.');

    if (!decimals || decimals.length === 0) return new BigNumber(int);
    return new BigNumber(`${int}.${decimals.slice(0, dp)}`);
  }

  public static renderString(callback: (formatter: typeof NumberFormatter) => NumberParts) {
    return this.renderNumberPartsToString(callback(NumberFormatter));
  }

  public static renderNumberPartsToString(value: NumberParts) {
    return (
      `${value.integer}` +
      (value.decimals ? `.` : '') +
      (value.startZeros > 0 ? Array(value.startZeros).fill('0').join('') : '') +
      (value.decimals ? `${value.decimals}` : '') +
      `${value.variant}`
    );
  }

  public static formatDelta(value: number): string {
    return value > 0 ? `+${value}` : value.toString();
  }
}
